import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { loadTemplate } from '../utils/templateManager';

const initialState = {
    report: {
        id: 'report-1',
        name: 'New Report',
        layout: {
            lg: [],
        },
        widgets: {},
        metadata: {
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            version: '1.0.0',
        },
    },
    isEditing: false,
    highlightedWidgetId: null,
};

function reportReducer(state, action) {
    switch (action.type) {
        case 'SET_REPORT':
            return {
                ...state,
                report: action.payload, // full replacement (used only for initial load or full resets)
            };

        case 'UPDATE_REPORT_NAME':
            return {
                ...state,
                report: {
                    ...state.report,
                    name: action.payload,
                    metadata: {
                        ...state.report.metadata,
                        updatedAt: new Date().toISOString(),
                    },
                },
            };

        case 'SET_EDIT_MODE':
            return {
                ...state,
                isEditing: action.payload,
            };

        case 'ADD_WIDGET': {
            const { widget, layout } = action.payload;
            const newLayout = {
                ...state.report.layout,
                lg: [...(state.report.layout.lg || []), layout],
            };
            return {
                ...state,
                report: {
                    ...state.report,
                    layout: newLayout,
                    widgets: {
                        ...state.report.widgets,
                        [widget.id]: widget,
                    },
                    metadata: {
                        ...state.report.metadata,
                        updatedAt: new Date().toISOString(),
                    },
                },
            };
        }

        case 'REMOVE_WIDGET': {
            const widgetId = action.payload;
            const { [widgetId]: removed, ...remainingWidgets } = state.report.widgets;
            const newLayout = {
                ...state.report.layout,
                lg: state.report.layout.lg.filter((item) => item.i !== widgetId),
            };
            return {
                ...state,
                report: {
                    ...state.report,
                    layout: newLayout,
                    widgets: remainingWidgets,
                    metadata: {
                        ...state.report.metadata,
                        updatedAt: new Date().toISOString(),
                    },
                },
            };
        }

        case 'UPDATE_WIDGET': {
            const { id, widget } = action.payload;
            return {
                ...state,
                report: {
                    ...state.report,
                    widgets: {
                        ...state.report.widgets,
                        [id]: {
                            ...state.report.widgets[id],
                            ...widget,
                        },
                    },
                    metadata: {
                        ...state.report.metadata,
                        updatedAt: new Date().toISOString(),
                    },
                },
            };
        }

        case 'UPDATE_LAYOUT': {
            const { breakpoint, layout } = action.payload;
            return {
                ...state,
                report: {
                    ...state.report,
                    layout: {
                        ...state.report.layout,
                        [breakpoint]: layout,
                    },
                    metadata: {
                        ...state.report.metadata,
                        updatedAt: new Date().toISOString(),
                    },
                },
            };
        }
        case 'UPDATE_REPORT': {
            return {
                ...state,
                report: {
                    ...state.report, // preserve current report fields
                    ...action.payload, // merge in top-level updates
                    widgets: { ...state.report.widgets }, // always preserve widgets
                    metadata: {
                        ...state.report.metadata,
                        updatedAt: new Date().toISOString(), // update timestamp
                    },
                },
            };
        }

        case 'UPDATE_GLOBAL_FILTERS': {
            return {
                ...state,
                report: {
                    ...state.report,
                    globalFilters: action.payload,
                    metadata: {
                        ...state.report.metadata,
                        updatedAt: new Date().toISOString(),
                    },
                },
            };
        }

        case 'HIGHLIGHT_WIDGET': {
            return {
                ...state,
                highlightedWidgetId: action.payload,
            };
        }

        default:
            return state;
    }
}

export const ReportContext = createContext(undefined);

export function ReportProvider({ children }) {
    const [state, dispatch] = useReducer(reportReducer, initialState);
    const [hasLoadedDefaultTemplate, setHasLoadedDefaultTemplate] = useState(false);
    const location = useLocation();

    // Determine which template to load based on URL path
    const getTemplateIdFromPath = () => {
        const pathname = location.pathname;
        if (pathname.includes('weekly-report')) {
            return 'weekly-report';
        } 
        // Default to annual-report-2025
        return 'annual-report-2025';
    };

    // Load template based on URL path when dashboard opens
    useEffect(() => {
        // Only load if report is empty (no widgets) and we haven't loaded a template yet
        if (!hasLoadedDefaultTemplate && Object.keys(state.report.widgets).length === 0) {
            const templateId = getTemplateIdFromPath();
            loadTemplate(templateId)
                .then((template) => {
                    // Generate new ID and update timestamps for the loaded template
                    const loadedReport = {
                        ...template,
                        id: `report-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        metadata: {
                            ...template.metadata,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                        },
                    };
                    dispatch({ type: 'SET_REPORT', payload: loadedReport });
                    setHasLoadedDefaultTemplate(true);
                })
                .catch((error) => {
                    console.error('Error loading default template:', error);
                    // If template loading fails, keep the empty report
                    setHasLoadedDefaultTemplate(true);
                });
        }
    }, [hasLoadedDefaultTemplate, state.report.widgets, location.pathname]);

    const addWidget = (widget, layout) => {
        dispatch({ type: 'ADD_WIDGET', payload: { widget, layout } });
    };

    const removeWidget = (widgetId) => {
        dispatch({ type: 'REMOVE_WIDGET', payload: widgetId });
    };

    const updateWidget = (id, widget) => {
        dispatch({ type: 'UPDATE_WIDGET', payload: { id, widget } });
    };

    const updateLayout = (breakpoint, layout) => {
        dispatch({ type: 'UPDATE_LAYOUT', payload: { breakpoint, layout } });
    };

    const updateReportName = (name) => {
        dispatch({ type: 'UPDATE_REPORT_NAME', payload: name });
    };

    const setEditMode = (isEditing) => {
        dispatch({ type: 'SET_EDIT_MODE', payload: isEditing });
    };

    const setReport = (report) => {
        dispatch({ type: 'SET_REPORT', payload: report });
    };

    const updateReport = (updates) => {
        dispatch({
            type: 'UPDATE_REPORT',
            payload: updates,
        });
    };

    const updateGlobalFilters = (filters) => {
        dispatch({ type: 'UPDATE_GLOBAL_FILTERS', payload: filters });
    };

    const highlightWidget = (widgetId) => {
        dispatch({ type: 'HIGHLIGHT_WIDGET', payload: widgetId });
    };

    return (
        <ReportContext.Provider
            value={{
                state,
                dispatch,
                addWidget,
                removeWidget,
                updateWidget,
                updateLayout,
                updateReportName,
                setEditMode,
                setReport,
                updateReport,
                updateGlobalFilters,
                highlightWidget,
            }}
        >
            {children}
        </ReportContext.Provider>
    );
}

export function useReport() {
    const context = useContext(ReportContext);
    if (context === undefined) {
        throw new Error('useReport must be used within a ReportProvider');
    }
    return context;
}
